<template>
  <div class="debug-screen" data-testid="debug-screen">
    <BzScreen title="Camera Debug" :back-label="$t('message.back')" back-testid="debug-back" @back="goBack">
      <!-- The design system (docs/sistema-diseno/README.md): its catalog, and the one piece of it that
           cannot be opened by hand anywhere else, the blocking notification. And which service screen
           this tablet paints, for trying the new one without the store's flag. The team's tools: on the
           previews and a laptop, never in production (src/devTools.js). -->
      <BzListGroup v-if="devTools" data-testid="debug-design-tools">
        <BzListRow action icon="palette" data-testid="debug-open-ui-catalog"
                   :label="$t('message.uiCatalogOpen')" @click="catalogOpen = true"/>
        <BzListRow action icon="notifications" data-testid="debug-sample-notification"
                   :label="$t('message.uiCatalogSampleNotification')" @click="openSampleNotification"/>
        <BzListRow icon="grid_view" data-testid="debug-lane"
                   :label="$t('message.debugLane')" :hint="$t('message.debugLaneHint')">
          <template #control="{ labelId, hintId }">
            <BzSelect :labelled-by="labelId" :described-by="hintId" :options="laneOptions"
                      :model-value="laneChoice" @update:model-value="chooseLane"/>
          </template>
        </BzListRow>
      </BzListGroup>

      <section class="debug-section">
        <h2 class="debug-heading">Media Devices</h2>
        <div class="debug-card">
          <div v-if="loading" class="debug-note">Analyzing media devices...</div>
          <div v-else-if="devices.length === 0" class="debug-error"><BzIcon name="error" /> No devices found</div>
          <template v-else>
            <div v-for="(device, index) in devices" :key="index" class="debug-device" data-testid="debug-device">
              <div class="debug-line"><span class="debug-key">Device ID:</span> {{ truncateId(device.deviceId) }}</div>
              <div class="debug-line"><span class="debug-key">Kind:</span> {{ device.kind }}</div>
              <div class="debug-line"><span class="debug-key">Label:</span> {{ device.label || 'Label not available' }}</div>
            </div>
          </template>
        </div>
      </section>

      <section class="debug-section">
        <h2 class="debug-heading">Camera Constraints Tests</h2>
        <div v-for="(test, index) in constraintTests" :key="index" class="debug-card debug-test" data-testid="debug-constraint-test" :data-status="test.status">
          <div class="debug-test__header">
            <h3 class="debug-test__name">Test {{ index + 1 }}</h3>
            <span v-if="test.status === 'pending'" class="bz-chip bz-chip--pending"><BzIcon name="schedule" /> Pending</span>
            <span v-else-if="test.status === 'success'" class="bz-chip bz-chip--ok"><BzIcon name="check_circle" /> Success</span>
            <span v-else-if="test.status === 'error'" class="bz-chip bz-chip--wrong"><BzIcon name="error" /> Failed</span>
            <span v-else class="bz-chip"><BzIcon name="progress_activity" class="bz-spinner" /> Testing...</span>
          </div>

          <pre class="debug-code">{{ JSON.stringify(test.constraints, null, 2) }}</pre>

          <div v-if="test.status === 'success'" class="debug-result">
            <h4 class="debug-subheading">Results:</h4>
            <div class="debug-line"><span class="debug-key">Width:</span> {{ test.width }}px</div>
            <div class="debug-line"><span class="debug-key">Height:</span> {{ test.height }}px</div>
            <div class="debug-line"><span class="debug-key">Device:</span> {{ test.deviceLabel || 'Unknown device' }}</div>
            <div class="debug-line"><span class="debug-key">Facing Mode:</span> {{ test.facingMode || 'Unknown' }}</div>
          </div>

          <div v-if="test.status === 'error'" class="debug-result">
            <h4 class="debug-subheading">Error:</h4>
            <div class="debug-error"><BzIcon name="error" /> {{ test.error }}</div>
          </div>
        </div>
      </section>

      <BzButton class="run-tests" data-testid="debug-run-tests" kind="primary" size="lg" block :disabled="testsRunning"
                :label="testsRunning ? 'Tests Running...' : 'Run Tests Again'" @click="runTests" />
    </BzScreen>
    <UiCatalog v-if="catalogOpen" @go-back="catalogOpen = false" />
    <!-- With no `endpoint`, an answer goes nowhere: NotificationComponent only calls the backend
         when the notification names one. -->
    <NotificationComponent :notification="sampleNotification"
                           @notification-closed="sampleNotification = null"
                           @option-selected="sampleNotification = null" />
  </div>
</template>

<script>
import Logger from '@/logger';
import BzButton from '@/ui/components/BzButton.vue';
import BzIcon from '@/ui/components/BzIcon.vue';
import BzListGroup from '@/ui/components/BzListGroup.vue';
import BzListRow from '@/ui/components/BzListRow.vue';
import BzScreen from '@/ui/components/BzScreen.vue';
import BzSelect from '@/ui/components/BzSelect.vue';
import { laneOverride, setLaneOverride } from '@/ui/lane';
import { devToolsEnabled } from '@/devTools';
import UiCatalog from '@/ui/catalog/UiCatalog.vue';
import NotificationComponent from './NotificationComponent.vue';

const logger = new Logger({screen: 'DebugScreen'});

// Android camera HALs do not free the sensor synchronously when a track is stopped: requesting it
// again in the same tick can fail with NotReadableError even though nothing holds it any more —
// producing the exact false negative this screen exists to rule out. Paid once per test, so the
// whole suite costs under a second of extra wait.
const CAMERA_RELEASE_SETTLE_MS = 300;

export default {
  name: 'DebugScreen',
  components: { BzButton, BzIcon, BzListGroup, BzListRow, BzScreen, BzSelect, UiCatalog, NotificationComponent },
  data() {
    return {
      // This tablet's own choice of service screen ('' = the store's flag decides).
      laneChoice: laneOverride(),
      devTools: devToolsEnabled(),
      catalogOpen: false,
      sampleNotification: null,
      loading: true,
      devices: [],
      testsRunning: false,
      constraintTests: [
        {
          constraints: {
            video: {
              width: { ideal: 4096 },
              resizeMode: "none",
              facingMode: { ideal: 'environment' }
            }
          },
          status: 'pending',
          width: null,
          height: null,
          deviceLabel: null,
          facingMode: null,
          error: null,
          stream: null
        },
        {
          constraints: {
            video: {
              resizeMode: "none",
              facingMode: { ideal: 'environment' }
            }
          },
          status: 'pending',
          width: null,
          height: null,
          deviceLabel: null,
          facingMode: null,
          error: null,
          stream: null
        },
        {
          constraints: {
            video: {
              facingMode: { ideal: 'environment' }
            }
          },
          status: 'pending',
          width: null,
          height: null,
          deviceLabel: null,
          facingMode: null,
          error: null,
          stream: null
        }
      ]
    };
  },
  computed: {
    laneOptions() {
      return [
        { value: '', label: this.$t('message.debugLaneStore') },
        { value: 'system', label: this.$t('message.debugLaneSystem') },
        { value: 'current', label: this.$t('message.debugLaneCurrent') },
      ];
    },
  },
  async mounted() {
    await this.enumerateDevices();
    // Run tests automatically when component is mounted
    this.runTests();
  },
  beforeUnmount() {
    // Clean up any active streams
    this.stopAllStreams();
  },
  methods: {
    // A notification as the backend sends one, with a countdown, to see it on the tablet.
    openSampleNotification() {
      this.sampleNotification = {
        id: `ui-catalog-sample-${Date.now()}`,
        source: 'Bronze',
        title: this.$t('message.uiCatalogSampleTitle'),
        body: this.$t('message.uiCatalogSampleBody'),
        options: [this.$t('message.uiCatalogSampleYes'), this.$t('message.uiCatalogSampleNo')],
        timeout: '30',
      };
    },
    // Stored for the next load, never applied to the open page (src/ui/lane.js): nothing may change
    // under a cook's finger, so the row says it applies when the app reloads and this does not reload.
    chooseLane(value) {
      setLaneOverride(value);
      this.laneChoice = laneOverride();
    },
    goBack() {
      this.$emit('go-back');
    },
    truncateId(id) {
      if (!id) return 'N/A';
      return id.length > 20 ? id.substring(0, 10) + '...' + id.substring(id.length - 10) : id;
    },
    async enumerateDevices() {
      try {
        this.loading = true;
        
        // First request camera permission to get device labels
        try {
          const tempStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
          // Stop the stream immediately after getting permission
          tempStream.getTracks().forEach(track => track.stop());
        } catch (err) {
          logger.warn('Could not get initial permission for device labels:', err);
        }
        
        // Now enumerate devices
        const devices = await navigator.mediaDevices.enumerateDevices();
        this.devices = devices.filter(device => device.kind === 'videoinput' || device.kind === 'audioinput');
        logger.info('Enumerated devices:', this.devices);
      } catch (error) {
        logger.error('Error enumerating devices', {error: error});
      } finally {
        this.loading = false;
      }
    },
    async runTests() {
      this.testsRunning = true;
      this.stopAllStreams();
      
      // Reset all tests to pending
      this.constraintTests.forEach(test => {
        test.status = 'pending';
        test.width = null;
        test.height = null;
        test.deviceLabel = null;
        test.facingMode = null;
        test.error = null;
        test.stream = null;
      });
      
      // Run tests sequentially. Each test must start with the camera free: on a single-camera
      // device a stream still held by an earlier test makes the next getUserMedia fail with
      // NotReadableError ("Could not start video source"), which reads exactly like the
      // constraints being rejected. Releasing before every request — not after a later success —
      // is what keeps the result attributable to the constraints under test.
      for (let i = 0; i < this.constraintTests.length; i++) {
        const test = this.constraintTests[i];
        test.status = 'running';
        this.stopAllStreams();
        await this.waitForCameraRelease();

        try {
          // Get user media with the constraints
          logger.info(`Running test ${i + 1} with constraints:`, test.constraints);
          const stream = await navigator.mediaDevices.getUserMedia(test.constraints);
          
          // Get video track and settings
          const videoTrack = stream.getVideoTracks()[0];
          if (videoTrack) {
            const settings = videoTrack.getSettings();
            logger.info(`Test ${i + 1} succeeded with settings:`, settings);
            
            test.status = 'success';
            test.width = settings.width;
            test.height = settings.height;
            test.deviceLabel = videoTrack.label;
            test.facingMode = settings.facingMode;
            // Recorded so the next iteration (and beforeUnmount) can release it. The settings above
            // are read first because stopping the track blanks them.
            test.stream = stream;
          } else {
            test.status = 'error';
            test.error = 'No video track found in the stream';
            if (stream) {
              stream.getTracks().forEach(track => track.stop());
            }
          }
        } catch (error) {
          logger.error(`Test ${i + 1} failed`, {error: error});
          test.status = 'error';
          test.error = error.message || 'Unknown error';
        }
      }

      // The final test's stream has no next iteration to release it, and leaving it open holds the
      // camera hostage for the rest of the app (the camera screen would then fail to start).
      this.stopAllStreams();

      this.testsRunning = false;
    },
    waitForCameraRelease() {
      return new Promise(resolve => setTimeout(resolve, CAMERA_RELEASE_SETTLE_MS));
    },
    stopAllStreams() {
      this.constraintTests.forEach(test => {
        if (test.stream) {
          test.stream.getTracks().forEach(track => track.stop());
          test.stream = null;
        }
      });
    }
  }
};
</script>

<style scoped>
/* Debug is on the design system (docs/sistema-diseno/README.md): its screen, its rows and its button are
   the library's, and what is here is only the diagnosis: cards of the surface, a code block and lines. */
.debug-screen {
  position: absolute;
  inset: 0;
}

.debug-section {
  margin: 0 0 var(--bz-space-7);
}

/* A section's name as a group's: 16 at 500 in the second ink, 8 from its card. */
.debug-heading {
  margin: 0 var(--bz-space-4) var(--bz-space-2);
  font-size: var(--bz-text-md);
  line-height: var(--bz-leading-md);
  letter-spacing: var(--bz-tracking-md);
  font-weight: var(--bz-weight-title);
  color: var(--bz-ink-2);
}

.debug-card {
  box-sizing: border-box;
  padding: var(--bz-space-4);
  border: 1px solid var(--bz-edge);
  border-radius: var(--bz-radius-lg);
  background: var(--bz-surface);
}

.debug-test + .debug-test {
  margin-top: var(--bz-target-gap);
}

.debug-device + .debug-device {
  margin-top: var(--bz-space-4);
  padding-top: var(--bz-space-4);
  border-top: 1px solid var(--bz-edge);
}

.debug-line {
  font-size: var(--bz-text-md);
  line-height: var(--bz-leading-md);
  letter-spacing: var(--bz-tracking-md);
  font-weight: var(--bz-weight-body);
  color: var(--bz-ink);
  overflow-wrap: anywhere;
}

.debug-key {
  color: var(--bz-ink-2);
}

.debug-note {
  font-size: var(--bz-text-md);
  line-height: var(--bz-leading-md);
  letter-spacing: var(--bz-tracking-md);
  color: var(--bz-ink-2);
}

/* A failure: the state's colour and its icon, never the colour alone (norm 11). */
.debug-error {
  display: flex;
  align-items: flex-start;
  gap: var(--bz-space-2);
  font-size: var(--bz-text-md);
  line-height: var(--bz-leading-md);
  letter-spacing: var(--bz-tracking-md);
  font-weight: var(--bz-weight-body);
  color: var(--bz-wrong);
  overflow-wrap: anywhere;
}

.debug-error .bz-icon {
  margin-top: 2px;
  font-size: var(--bz-icon-sm);
}

.debug-test__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--bz-space-3);
  margin-bottom: var(--bz-space-3);
}

.debug-test__name {
  margin: 0;
  font-size: var(--bz-text-md);
  line-height: var(--bz-leading-md);
  letter-spacing: var(--bz-tracking-md);
  font-weight: var(--bz-weight-title);
  color: var(--bz-ink);
}

/* The constraints as sent, in the monospace of the browser's own <pre> (the system has no token for one). */
.debug-code {
  margin: 0;
  padding: var(--bz-space-3);
  border: 1px solid var(--bz-edge);
  border-radius: var(--bz-radius-md);
  background: var(--bz-bg);
  color: var(--bz-ink);
  font-size: var(--bz-text-sm);
  line-height: var(--bz-leading-sm);
  letter-spacing: var(--bz-tracking-sm);
  overflow-x: auto;
  white-space: pre-wrap;
}

.debug-result {
  margin-top: var(--bz-space-3);
}

.debug-subheading {
  margin: 0 0 var(--bz-space-1);
  font-size: var(--bz-text-md);
  line-height: var(--bz-leading-md);
  letter-spacing: var(--bz-tracking-md);
  font-weight: var(--bz-weight-title);
  color: var(--bz-ink-2);
}
</style>
